import { Database, Calculator, Sparkles, UserCheck, AlertCircle } from "lucide-react";

/**
 * DataStatusChip — visual provenance tag per the approved scope §6.
 *
 * Statuses:
 *   source            → Directly from a source system (AroFlo/Xero/MYOB/…)
 *   calculated        → Computed by GBE Hub (formula-based)
 *   ai_generated      → Inferred by an AI model
 *   manager_adjusted  → Manually overridden / approved by a manager
 *   ai_pending        → "AI Extracted — Pending Verification" (contracts, etc.)
 *
 * Usage:
 *   <DataStatusChip status="ai_generated" />
 *   <DataStatusChip status="source" label="Xero · live" size="xs" />
 */
const STATUS_CONFIG = {
  source: {
    label: "Source",
    Icon: Database,
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    title: "Directly retrieved from an approved source system",
  },
  calculated: {
    label: "Calculated",
    Icon: Calculator,
    tone: "bg-blue-50 text-blue-700 border-blue-200",
    title: "Computed by GBE Hub using a defined formula",
  },
  ai_generated: {
    label: "AI Estimate",
    Icon: Sparkles,
    tone: "bg-violet-50 text-violet-700 border-violet-200",
    title: "Inferred by an AI model — treat as an estimate",
  },
  manager_adjusted: {
    label: "Manager Adjusted",
    Icon: UserCheck,
    tone: "bg-emerald-50 text-emerald-700 border-emerald-200",
    title: "Manually approved or adjusted by an authorised user",
  },
  ai_pending: {
    label: "AI · Pending Verification",
    Icon: AlertCircle,
    tone: "bg-amber-50 text-amber-800 border-amber-200",
    title: "Extracted by AI — awaiting human verification",
  },
};

export function DataStatusChip({ status = "source", label, size = "sm", className = "" }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.source;
  const { Icon } = cfg;
  const px = size === "xs" ? "px-1.5 py-0.5 text-[10px]"
           : size === "md" ? "px-2.5 py-1 text-xs"
           :                  "px-2 py-0.5 text-[11px]";
  const iconSize = size === "xs" ? "w-2.5 h-2.5" : size === "md" ? "w-3.5 h-3.5" : "w-3 h-3";
  return (
    <span
      data-testid={`data-status-chip-${status}`}
      title={cfg.title}
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${cfg.tone} ${px} ${className}`}
    >
      <Icon className={iconSize} />
      <span className="whitespace-nowrap">{label ?? cfg.label}</span>
    </span>
  );
}

export const DATA_STATUS_OPTIONS = [
  { value: "source",            ...STATUS_CONFIG.source },
  { value: "calculated",        ...STATUS_CONFIG.calculated },
  { value: "ai_generated",      ...STATUS_CONFIG.ai_generated },
  { value: "manager_adjusted",  ...STATUS_CONFIG.manager_adjusted },
  { value: "ai_pending",        ...STATUS_CONFIG.ai_pending },
];
